import { merge } from 'lodash';
import { compileExpression } from 'filtrex';
import { BehaviorSubject, ReplaySubject, Subject, combineLatest } from 'rxjs';
import { takeUntil, startWith, tap } from 'rxjs/operators';

class AclEvaluationBase {
    get rootTag() {
        return '';
    }
    get unscoped() {
        return this;
    }
    canShow$(aclPath) {
        throw new Error('Implement me');
    }
    canEdit$(aclPath) {
        throw new Error('Implement me');
    }
    canShow(aclPath) {
        throw new Error('Implement me');
    }
    canEdit(aclPath) {
        throw new Error('Implement me');
    }
}

/**
 * A decision request that holds the subject, resource and the requested action.
 * This data object is constructed for every evaluation of a given resource.
 *
 * Naming & Implementation (loosely) based on http://docs.oasis-open.org/xacml/3.0/xacml-3.0-core-spec-os-en.html
 */
class DecisionRequest {
    constructor(subject, resource, action) {
        this.subject = subject;
        this.resource = resource;
        this.action = action;
    }
    static create(...args) {
        if (isTuple$1(args)) {
            return new DecisionRequest(...args);
        }
        else {
            return new DecisionRequest(args[0].subject, args[0].resource, args[0].action);
        }
    }
    toString() {
        return `s: ${this.subject}, r: ${this.resource}, a: ${this.action}`;
    }
    toArray() {
        return [this.subject, this.resource, this.action];
    }
}
/**
 * Some TS type magic experiment, so we can have one constructor with parameters
 * and then extract those parameters (here: subject, resource, action) with the matching types
 * and allow to create a static convenience method 'create' to reuse the types and offer an alternative
 * way to create the class. This is experimental and might be removed any time.
 */
function isTuple$1(value) {
    return typeof value[0] !== 'object';
}

const WILDCARD = '*';
const NOT_APPLICABLE = 'not-applicable';
var Action;
(function (Action) {
    Action["View"] = "view";
    Action["Edit"] = "edit";
    Action["Any"] = "*";
})(Action || (Action = {}));
var Effect;
(function (Effect) {
    Effect["Permit"] = "allow";
    Effect["Deny"] = "deny";
    Effect["Any"] = "*";
})(Effect || (Effect = {}));

class AclRule {
    constructor(id, subject, resource, condition, action, effect, active) {
        this.id = id;
        this.subject = subject;
        this.resource = resource;
        this.condition = condition;
        this.action = action;
        this.effect = effect;
        this.active = active;
    }
    static create(...args) {
        var _a, _b, _c, _d, _e, _f, _g;
        if (isTuple(args)) {
            return new AclRule(...args);
        }
        else {
            return new AclRule((_a = args[0].id) !== null && _a !== void 0 ? _a : null, (_b = args[0].subject) !== null && _b !== void 0 ? _b : WILDCARD, (_c = args[0].resource) !== null && _c !== void 0 ? _c : WILDCARD, (_d = args[0].condition) !== null && _d !== void 0 ? _d : null, (_e = args[0].action) !== null && _e !== void 0 ? _e : Action.View, (_f = args[0].effect) !== null && _f !== void 0 ? _f : Effect.Permit, (_g = args[0].active) !== null && _g !== void 0 ? _g : true);
        }
    }
    serialize() {
        var _a;
        const prefix = false === this.active ? '# ' : '';
        return `${prefix}${this.subject}, ${this.resource}, ${(_a = this.condition) !== null && _a !== void 0 ? _a : ''}, ${this.action}, ${this.effect}`;
    }
    clone() {
        return AclRule.create({
            id: this.id,
            subject: this.subject,
            resource: this.resource,
            condition: this.condition,
            action: this.action,
            effect: this.effect,
            active: this.active
        });
    }
    update(property, value) {
        if (property === 'subject' || property === 'resource') {
            this[property] = value !== null && value !== void 0 ? value : WILDCARD;
            return;
        }
        if (property === 'action') {
            this[property] = value;
            return;
        }
        if (property === 'effect') {
            this[property] = value;
            return;
        }
        this[property] = value;
    }
}
function isTuple(value) {
    return typeof value[0] !== 'object';
}

const withEnvironment = (engine, aclEnvironment) => (expression, options = {}, environment = {}) => {
    /**
     * merge anything added to environment
     * with the data coming from AclEnvironment
     */
    const newEnvironment = merge(aclEnvironment.getShallowMap(), environment);
    return engine(expression, Object.assign({}, options), newEnvironment);
};

/**
 * Extend the options to provide access to the given store function
 * by using s("your-key") in the expression. 's' will invoke
 * the 'queryStoreValue' of the given ExpressionStoreAdapter
 */
const withStore = (engine, storeAdapter) => (expression, options = {}, environment = {}) => {
    const provideStoreAccessKeyword = {
        extraFunctions: { s: storeAdapter.queryStoreValue }
    };
    const updatedOptions = merge(options, provideStoreAccessKeyword);
    return engine(expression, updatedOptions, environment);
};

class AclEnvironment {
    constructor() {
        this.map = new Map();
    }
    set(key, value) {
        if (containsObjectWithKeys(value)) {
            throw new Error(`Given value contains nested properties ${value}`);
        }
        this.map.set(key, value);
    }
    get(key) {
        return this.map.get(key);
    }
    delete(key) {
        this.map.delete(key);
    }
    getShallowMap() {
        const entries = Array.from(this.map.entries());
        const result = entries.reduce((accu, [key, value]) => {
            accu[`env.${key}`] = value;
            return accu;
        }, {});
        try {
            assertShallowEnvironmentMap(result);
        }
        catch (error) {
            throw new Error('ACL Environment failed to transform internal map as some');
        }
        return result;
    }
}
function containsObjectWithKeys(value) {
    return value && typeof value === 'object' && Object.keys(value).length > 0;
}
/**
 * for security reasons we can only pass a flat environment map to the expression engine.
 * This means if you have an object structure like `a { b { c = 1}}` you need to generate
 * an environment with the  key {`a.b.c` = 1}. The engine will refuse to access nested objects
 * and file silently.
 */
function assertShallowEnvironmentMap(data = {}) {
    const erroneousKeys = Object.entries(data)
        .filter(([, value]) => {
        return containsObjectWithKeys(value);
    })
        .map(([key]) => key);
    if (erroneousKeys.length > 0) {
        throw new Error(`Given object has a non-flat key. Check the following keys [${erroneousKeys}]`);
    }
}

/**
 * Base function that will invoke the underlying expression engine from 'filtrex'
 */
const createExpressionEngine = (myOptions = {}, myEnvironment = {}) => (expression, options = {}, environment = {}) => {
    const currentOptions = merge(options, myOptions);
    const currentEnvironment = merge(environment, myEnvironment);
    try {
        assertShallowEnvironmentMap(currentEnvironment);
    }
    catch (error) {
        throw new Error(`Expression Engine received a non-shallow environment map ${currentEnvironment}`);
    }
    return !!compileExpression(expression, currentOptions)(currentEnvironment);
};

/**
 * Subjects will only match through equality but can match a wildcard sign too
 */
function matchSubject(ruleSubject, decisionSubject) {
    return ruleSubject === '*' || ruleSubject === decisionSubject;
}

/**
 * Create a regex to match glob like strings.
 * my/resource/path matches 'resources/*'
 *
 * Overly simplified. A star (*) matches virtually everything.
 * No difference between one statr and two stars.
 * No brace expansion. No extglob syntax.
 *
 * Reasons for this instead of using a proper glob library:
 * Friends like micromatch, picomatch & nanomatch rely on `fs` which
 * causes quite some trouble in browsers as bundlers like webpack are usually not properly configured
 * (especially in our target Angular environment) to handle 'fs', 'path' etc.
 *
 * They are one of the reasons we decided for the first version to reimplement
 * the core principles of casbin instead of just using it.
 *
 * To get into globbing mood check the page to tinker around:
 * https://globster.xyz/
 */
function globMatch(value, pattern) {
    const regexPattern = wildcardToRegExp(pattern);
    const result = regexPattern.test(value);
    return result;
}
/**
 * This regex is not written by us, but found somewhere in the guts of the internet.
 * Unfortunately we did not record the exact source to provide proper credits.
 */
function wildcardToRegExp(s) {
    return new RegExp('^' + s.split(/\*+/).map(regExpEscape).join('.*') + '$');
}
function regExpEscape(s) {
    return s.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');
}

function matchResource(ruleResource, decisionResource) {
    if (!ruleResource || !decisionResource) {
        return false;
    }
    return globMatch(decisionResource, ruleResource);
}

function matchAction(ruleAction, decisionAction) {
    return ruleAction === '*' || ruleAction === decisionAction;
}

/**
 * Target evaluation to determine if subject, resource & action are matching.
 * If they are not matching, then the rule is not relevant for the given DecisionRequest
 *
 * All targets are matching -> Match (false)
 * At least one “No Match” -> No Match (false)
 *
 * No "Indeterminate" state as in the standard as our policy won't
 * handle indeterminated results.
 *
 * via OASIS: 7.7 Target evaluation
 * http://docs.oasis-open.org/xacml/3.0/xacml-3.0-core-spec-os-en.html

 */
function matchTarget(rule, decisionRequest) {
    const subjectMatched = matchSubject(rule.subject, decisionRequest.subject);
    const resourceMatched = matchResource(rule.resource, decisionRequest.resource);
    const actionMatched = matchAction(rule.action, decisionRequest.action);
    const targetMatch = subjectMatched && resourceMatched && actionMatched;
    return targetMatch;
}

/**
 * Evaluate a single rule.
 * A) Evaluate the target (does subject & resource & action match)
 * B) (Optional) Evaluate the condition
 * If target matches & condition is true or not given then return the effect otherwise NOT_APPLICABLE
 */
function evaluateRule(rule, decisionRequest, conditionEvaluatorFn) {
    const targetMatch = matchTarget(rule, decisionRequest);
    let conditionResult = NOT_APPLICABLE;
    // console.log(`Analyse Rule #${rule.id}`);
    if (targetMatch) {
        conditionResult = evaluateCondition(rule.condition, conditionEvaluatorFn);
    }
    return calculateDecision(targetMatch, conditionResult, rule.effect);
}
function calculateDecision(targetMatch, conditionResult, effect) {
    switch (targetMatch) {
        case true:
            switch (conditionResult) {
                case true:
                case NOT_APPLICABLE:
                    return effect;
                    break;
                case false:
                    return NOT_APPLICABLE;
                    break;
            }
            break;
        case false:
            return NOT_APPLICABLE;
            break;
    }
}
/**
 * Optionally handles a given an evaluator for a rule condition.
 * Either return the expression result (true/false) or return NOT_APPLICABLE
 * to let any consumer of this function decide how to handle the absence.
 * Usually this defaults to the same handling as a truthy evaluation
 * to allow rules with optional conditions.
 */
function evaluateCondition(condition, conditionEvaluatorFn) {
    if (!condition || !conditionEvaluatorFn) {
        return NOT_APPLICABLE;
    }
    // console.log(`-- Request Condition Check`, condition);
    return conditionEvaluatorFn(condition) === true;
}

/**
 * Implement XACML C.8 First-applicable
 * with a default to deny
 */
function evaluatePolicy(rules, decisionRequest, conditionEvaluator) {
    let result = NOT_APPLICABLE;
    // console.groupCollapsed(`Evaluation for ${decisionRequest.toString()}`);
    for (const rule of rules) {
        const ruleDecision = evaluateRule(rule, decisionRequest, conditionEvaluator);
        if (ruleDecision === Effect.Deny) {
            result = Effect.Deny;
            break; // exit the loop
        }
        if (ruleDecision === Effect.Permit) {
            result = Effect.Permit;
            break; // exit the loop
        }
        if (ruleDecision === NOT_APPLICABLE) {
            continue;
        }
    }
    // console.groupEnd();
    /**
     * Default to deny
     */
    if (result === NOT_APPLICABLE) {
        return Effect.Deny;
    }
    return result;
}

const LINE_DELIMITER = '\n';
const RULE_DELIMITER = /,(?![^(]*\))/g;
const RULE_FIELD_COUNT = 5;
const COMMENT_MARKER = '#';
const INACTIVE_RULE_MARKER = '##';
function parsePolicyDocument(policyString) {
    const lines = policyString
        .split(LINE_DELIMITER)
        .map((line) => line.trim())
        .filter((line) => false === isEmptyLine(line))
        .filter((line) => isRule(line))
        .reverse();
    const items = lines.map((item) => {
        const { active, lineParts: [subject, resource, condition, action, effect] } = createRuleFromPolicyLine(item);
        return {
            active,
            subject: valueOrNull(subject),
            resource: valueOrNull(resource),
            condition: valueOrNull(condition),
            action: getActionFromString(action),
            effect: getEffectFromString(effect)
        };
    });
    if (items.length > 0 && items.every((item) => item.subject === 'p')) {
        throw new Error(`This policy looks like a legacy policy where all rules are prefixed with "p, ".
Please provide a valid policy format. See the ACL guide for further information.`);
    }
    return items;
}
function createRuleFromPolicyLine(line) {
    const inactiveRule = isInactiveRule(line);
    if (inactiveRule) {
        line = stripMarker(line, INACTIVE_RULE_MARKER);
    }
    const lineParts = splitLine(line).map((item) => item.trim());
    const segmentCount = lineParts.length;
    if (segmentCount > RULE_FIELD_COUNT) {
        // We need to log the error message to the console manually because when this is used inside
        // a web component the error is swallowed.
        console.error(`The following ACL rule contains too many fields. Expected ${RULE_FIELD_COUNT} fields, but received ${segmentCount} fields.
  "${line}"`);
        throw new Error('Error in the ACL policy. Can not continue.');
    }
    if (segmentCount < RULE_FIELD_COUNT) {
        // We need to log the error message to the console manually because when this is used inside
        // a web component the error is swallowed.
        console.error(`The following ACL rule contains not enough fields. Expected ${RULE_FIELD_COUNT} fields, but received ${segmentCount} fields.
  "${line}"`);
        throw new Error('Error in the ACL policy. Can not continue.');
    }
    return {
        lineParts,
        active: inactiveRule === false
    };
}
function splitLine(line) {
    return line.split(RULE_DELIMITER);
}
function isEmptyLine(line) {
    return line.trim() === '';
}
function isRule(line) {
    return false === isComment(line) || isInactiveRule(line);
}
function isComment(line) {
    return line.substr(0, COMMENT_MARKER.length) === COMMENT_MARKER;
}
function isInactiveRule(line) {
    return line.substr(0, INACTIVE_RULE_MARKER.length) === INACTIVE_RULE_MARKER;
}
function stripMarker(line, marker) {
    return line.substring(marker.length);
}
function valueOrNull(value) {
    return value.trim() === '' ? null : value;
}
function getActionFromString(value) {
    switch (value) {
        case Action.View:
            return Action.View;
        case Action.Edit:
            return Action.Edit;
        case Action.Any:
            return Action.Any;
        default:
            return null;
    }
}
function getEffectFromString(value) {
    switch (value) {
        case Effect.Permit:
            return Effect.Permit;
        case Effect.Deny:
            return Effect.Deny;
        case Effect.Any:
            return Effect.Any;
        default:
            return null;
    }
}

class Policy {
    constructor(rules = []) {
        this._ruleCounter = 0;
        this._rules = [];
        this._rules = rules;
    }
    static parseDocument(content) {
        const policy = new Policy();
        const ruleData = parsePolicyDocument(content);
        for (const rule of ruleData) {
            policy.createRule(rule);
        }
        return policy;
    }
    static fromRules(rules) {
        const policy = new Policy();
        rules.forEach((rule) => policy.createRule(rule));
        return policy;
    }
    reset() {
        this._rules.length = 0;
    }
    prepend(rules) {
        this.rules.unshift(...rules);
    }
    loadDocument(content) {
        try {
            const ruleData = parsePolicyDocument(content);
            this.reset();
            ruleData.forEach((data) => this.createRule(data));
        }
        catch (error) {
            alert('Failed to read document');
        }
    }
    reorder(rule, newIndex) {
        const oldIndex = this.getIndexOf(rule);
        this.rules.splice(newIndex, 0, this.rules.splice(oldIndex, 1)[0]);
    }
    get rules() {
        return this._rules;
    }
    getIndexOf(rule) {
        return this.rules.findIndex((item) => item.id === rule.id);
    }
    createRule(ruleData) {
        const rule = AclRule.create(Object.assign({ id: this._ruleCounter++ }, ruleData));
        this._rules.unshift(rule);
        return rule;
    }
    duplicate(rule) {
        const newRule = rule.clone();
        newRule.id = this._ruleCounter++;
        const index = this.getIndexOf(rule);
        this.rules.splice(index, 0, newRule);
        return newRule;
    }
    remove(rule) {
        const index = this._rules.findIndex((item) => item.id === rule.id);
        if (index !== -1) {
            this._rules.splice(index, 1);
        }
    }
    toDocument() {
        return this.rules.map((rule) => rule.serialize()).join('\n');
    }
    empty() {
        return this.rules.length === 0;
    }
}

/**
 * Policy decision point
 */
class PDP {
    constructor(_expressionEngine) {
        this._expressionEngine = _expressionEngine;
    }
    prepend(rules) {
        this._policy.prepend(rules);
    }
    loadRules(rules) {
        const policy = Policy.fromRules(rules);
        this.setPolicy(policy);
    }
    loadPolicyContent(content) {
        try {
            this.setPolicy(Policy.parseDocument(content));
        }
        catch (e) {
            throw new Error(`Could not load given policy content with reason '${e.message}'`);
        }
    }
    evaluate(request) {
        const result = evaluatePolicy(this.rules, request, this._expressionEngine);
        return result;
    }
    /**
     * Checks if at least one rule exists for the action of the resource that has a condition.
     */
    hasRuleWithCondition(action, resource) {
        return this.rules.some((rule) => {
            // Only the resource and action are taken into consideration to determine
            // if a rule with conditions exists. The subject, which is also used in matchTarget, is dynamic
            // (part of the environment) and therefore has to be ignored here.
            // If the subject is changed during runtime, a force refresh/full cache refresh
            // is triggered automatically, which means static rules are also covered.
            const resourceMatched = matchResource(rule.resource, resource);
            const actionMatched = matchAction(rule.action, action);
            return resourceMatched && actionMatched && rule.condition;
        });
    }
    setPolicy(policy) {
        this._policy = policy;
    }
    get rules() {
        var _a, _b;
        const rules = (_b = (_a = this._policy) === null || _a === void 0 ? void 0 : _a.rules) !== null && _b !== void 0 ? _b : [];
        return rules.filter((item) => item.active);
    }
    get policy() {
        return this._policy;
    }
}

const MAX_LENGTH = 250;
class Reporter {
    constructor() {
        this._items = [];
        /**
         * map of all seen resources with a list of actions
         */
        this._resourceMap = new Map();
    }
    log(decisionRequest, decision) {
        const item = { decisionRequest, decision, time: `${+new Date()}` };
        this._items.unshift(item);
        this.registerResource(item.decisionRequest);
        // trim to prevent infinite logs
        this._items.splice(MAX_LENGTH, this._items.length);
    }
    clear() {
        this._items.length = 0;
        this._resourceMap.clear();
    }
    registerResource(decisionRequest) {
        if (!this._resourceMap.has(decisionRequest.resource)) {
            this._resourceMap.set(decisionRequest.resource, new Set());
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const actionSet = this._resourceMap.get(decisionRequest.resource);
        actionSet.add(decisionRequest.action);
    }
    get items() {
        return this._items;
    }
    get resourceMap() {
        return this._resourceMap;
    }
}

/**
 * @param {(...args: QUERY_PARAMS) => boolean} isStaticFn A function that is called when a cache entry is created.
 * If this function returns true, a cache entry is assumed to be static and is never refreshed.
 * If the function is omitted, the entry is assumed to be dynamic
 */
const createCacheBox = (queryFn, isStaticFn) => {
    const cacheMap = new Map();
    const createKey = (...args) => args.join('~~~');
    /**
     * This function allows you to refresh the entire cache
     * by going over all cache entries, invoke 'queryFn' and override the stored
     * value with the freshly obtained one.
     * By default it will only refresh entries that are not considered static. (Determined via the isStaticFn())
     *
     * @param {boolean} [force=false] Force a refresh on all entries, even when they are flagged as static.
     */
    const refreshFn = (force = false) => {
        for (const cacheEntry of cacheMap.values()) {
            cacheEntry.refresh && cacheEntry.refresh(force);
        }
    };
    /**
     * This function allows to invalidate all entries in the cache.
     * This forces a refresh of the entry, as soon as it is requested from the cache.
     */
    const invalidateFn = () => {
        for (const cacheEntry of cacheMap.values()) {
            cacheEntry.invalid = true;
        }
    };
    /**
     * That's the main part being invoked for every query request
     *
     * The internal value is wrapped by a BehaviourSubject.
     * The BehaviourSubject is guaranteed to stay the same across multiple invocations with the same
     * combination of QUERY_PARAMS, matched by a constructed key of the individual query parts.
     *
     * When the cache is hit return the BehaviourSubject matching the given QUERY_PARAMS
     *
     */
    const requestFn = (...args) => {
        const key = createKey(...args);
        if (false === cacheMap.has(key)) {
            const cacheEntry = createCacheEntry();
            cacheMap.set(key, cacheEntry);
        }
        function createCacheEntry() {
            /**
             * Invoke the refresh fn again and forward the result
             * into the stored BehaviorSubject which will notify
             * subscribed consumers.
             */
            const getValue = () => {
                const value = queryFn(...args);
                return value;
            };
            const isStatic = isStaticFn(...args);
            const cachedSubject = new BehaviorSubject(getValue());
            // eslint-disable-next-line prefer-const
            let cacheEntry;
            const refresh = (force) => {
                cacheEntry.invalid = false;
                if (isStatic && !force) {
                    return;
                }
                const nextValue = getValue();
                if (cachedSubject.value !== nextValue) {
                    cachedSubject.next(nextValue);
                }
            };
            cacheEntry = {
                subject: cachedSubject,
                refresh: refresh,
                query: args,
                invalid: false
            };
            return cacheEntry;
        }
        const cacheEntry = cacheMap.get(key);
        if (cacheEntry === null || cacheEntry === void 0 ? void 0 : cacheEntry.invalid) {
            // Currently a refresh is a synchronous operation with all ExpressionStoreAdapter implementations, that we use.
            // So we can just call it directly here and return the same subject below.
            cacheEntry.refresh();
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const { subject } = cacheMap.get(key);
        return {
            value: subject.getValue(),
            observable: subject.asObservable()
        };
    };
    return {
        request: requestFn,
        refresh: refreshFn,
        invalidate: invalidateFn
    };
};

const SUBJECT_ACCESSOR = 'subject';
function createEngineWithStoreAndEnvironment(environment, storeAdapter) {
    if (storeAdapter) {
        return withEnvironment(withStore(createExpressionEngine(), storeAdapter), environment);
    }
    else {
        return withEnvironment(createExpressionEngine(), environment);
    }
}
class AclEngine extends AclEvaluationBase {
    constructor(storeAdapter) {
        super();
        this.environment = new AclEnvironment();
        const expressionEngine = createEngineWithStoreAndEnvironment(this.environment, storeAdapter);
        this.pdp = new PDP(expressionEngine);
        this.reporter = new Reporter();
        /**
         * This utility enables us to cache queries and its the foundation for our async canShow & canEdit$ methods
         * as it's returning matching Behaviour Subjects.
         */
        this.aclCache = createCacheBox((action, path) => {
            return this.can(action, path);
        }, (action, path) => {
            // This function is used to determine whether a rule is static and thus can be cached forever.
            // We consider all rules without a condition to be static.
            return !this.pdp.hasRuleWithCondition(action, path);
        });
    }
    refreshCache(force) {
        this.aclCache.refresh(force);
    }
    invalidateCache() {
        this.aclCache.invalidate();
    }
    /**
     * Set a policy. Automatically force refresh the cache to ensure everything is updated.
     */
    setPolicy(policy) {
        this.pdp.loadPolicyContent(policy);
        this.refreshCache(true);
    }
    /**
     * Allows it to inject rules during runtime. Should only be used for purposes like dev tools and not production code.
     * Automatically force refreshes the cache.
     */
    injectRules(rules) {
        console.warn('⚠️ You are injecting acl rules. Do not use this in production.');
        this.pdp.prepend(rules);
        this.refreshCache(true);
    }
    evaluate(decisionRequest) {
        var _a;
        const result = (_a = this.pdp) === null || _a === void 0 ? void 0 : _a.evaluate(decisionRequest);
        this.reporter.log(decisionRequest, result);
        return result === Effect.Permit;
    }
    can(action, resource) {
        const decisionRequest = DecisionRequest.create({
            subject: this.getEnvironmentValue(SUBJECT_ACCESSOR),
            resource,
            action
        });
        return this.evaluate(decisionRequest);
    }
    /**
     * The subject is stored as a special value in the environment.
     * Changing the subject triggers a force refresh on all cached
     * ACL rules as a change of the subject also affects static rules.
     */
    setSubject(value) {
        this.setEnvironmentValue(SUBJECT_ACCESSOR, value, true);
    }
    /**
     * Set an environment value for the rules.
     * Automatically refreshes the cache.
     * No force refresh is done on the cache as the environment is accessed in expressions:
     * Those rules are already considered dynamic, no force necessary
     */
    setEnvironmentValue(key, value, forceRefresh) {
        this.environment.set(key, value);
        this.refreshCache(forceRefresh);
    }
    getEnvironment() {
        return this.environment;
    }
    getEnvironmentValue(key) {
        return this.environment.get(key);
    }
    getPdp() {
        return this.pdp;
    }
    get policy() {
        return this.pdp.policy;
    }
    /** Implement ACL Evaluation Interface **/
    canShow$(aclPath) {
        const { observable } = this.aclCache.request(Action.View, aclPath);
        return observable;
    }
    canEdit$(aclPath) {
        const { observable } = this.aclCache.request(Action.Edit, aclPath);
        return observable;
    }
    canShow(aclPath) {
        const { value } = this.aclCache.request(Action.View, aclPath);
        return value;
    }
    canEdit(aclPath) {
        const { value } = this.aclCache.request(Action.Edit, aclPath);
        return value;
    }
}

class AclTag {
    constructor(tagName, parent, transient) {
        this.parent = parent;
        this.transient$ = new BehaviorSubject(false);
        this._aclKeySubject = new ReplaySubject(1);
        this._changed$ = new Subject();
        this._destroySignal$ = new Subject();
        if (typeof tagName === 'string') {
            tagName = new BehaviorSubject(tagName);
        }
        if (transient) {
            this.transient$ = transient;
        }
        this.tagName$ = tagName;
        this.subscribeToChanges();
    }
    static joinKeys(...keys) {
        return keys.join('/');
    }
    get aclKey$() {
        return this._aclKeySubject.asObservable().pipe(takeUntil(this._destroySignal$));
    }
    destroy() {
        this._destroySignal$.next();
    }
    get changed$() {
        return this._changed$.asObservable();
    }
    /**
     * wait for parent changes or own tag name changes and
     * calculate tag name for each change
     */
    subscribeToChanges() {
        var _a, _b;
        const parentList = ((_b = (_a = this.parent) === null || _a === void 0 ? void 0 : _a.changed$) !== null && _b !== void 0 ? _b : new Subject()).pipe(startWith([null]));
        combineLatest([this.tagName$, parentList, this.transient$])
            .pipe(tap(() => {
            this.update();
        }), takeUntil(this._destroySignal$))
            .subscribe();
    }
    notifyChildren() {
        this._changed$.next();
    }
    update() {
        // write current acl key intro the stream
        this._aclKeySubject.next({
            aclKey: this.aclKey,
            ownKey: this.ownTag,
            parentKeys: this.parentList
        });
        // notify dependent acl tags
        this.notifyChildren();
    }
    get aclKey() {
        if (this.isTransient) {
            return AclTag.joinKeys(...this.parentList, this.ownTag);
        }
        return AclTag.joinKeys(...this.tagList);
    }
    get ownTag() {
        return this.tagName$.getValue();
    }
    get isTransient() {
        return this.transient$.getValue();
    }
    get tagList() {
        if (this.isTransient) {
            return this.parentList;
        }
        return this.parentList.concat(this.ownTag);
    }
    get parentList() {
        var _a, _b;
        return [...((_b = (_a = this.parent) === null || _a === void 0 ? void 0 : _a.tagList) !== null && _b !== void 0 ? _b : [])];
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { AclEngine, AclEnvironment, AclEvaluationBase, AclRule, AclTag, Action, DecisionRequest, Effect, NOT_APPLICABLE, PDP, Policy, Reporter, WILDCARD, assertShallowEnvironmentMap, createCacheBox, createExpressionEngine, evaluatePolicy, evaluateRule, globMatch, matchAction, matchResource, matchSubject, matchTarget, parsePolicyDocument, withEnvironment, withStore };
